import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Eye, Heart, Trash2, Pencil, Plus, Check, Star, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { filterCss } from "@/lib/mediaEffects";

/* ============================ Story-Insights (Zuschauer) ============================ */
export function StoryInsightsSheet({ storyId, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSlide, setOpenSlide] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/studio/social/stories/${storyId}/insights`);
        setData(res.data);
      } catch (_) { setData({ slides: [], total_unique_viewers: 0 }); }
      finally { setLoading(false); }
    })();
  }, [storyId]);

  return (
    <div data-testid="story-insights-sheet" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0]">
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("stories_extra.insights_title", { defaultValue: "Story-Insights" })}</p>
          <button data-testid="story-insights-close" onClick={onClose} className="text-[#999] hover:text-[#0A0A0A]"><X size={18} /></button>
        </div>
        {loading ? (
          <p className="text-center text-xs text-[#999] py-10">…</p>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-xs text-[#666]">
              {t("stories_extra.insights_total", { count: data.total_unique_viewers, defaultValue: `${data.total_unique_viewers} eindeutige Zuschauer gesamt` })}
            </p>
            {(data.slides || []).map((sl, i) => (
              <div key={sl.media_url} data-testid={`insights-slide-${i}`} className="border border-[#EEE] rounded-xl overflow-hidden">
                <button onClick={() => setOpenSlide(openSlide === i ? -1 : i)} className="w-full flex items-center gap-3 p-2 hover:bg-[#FAFAFA]">
                  {sl.media_type === "VIDEO"
                    ? <video src={sl.media_url} className="w-12 h-12 rounded-lg object-cover" muted playsInline preload="metadata" />
                    : <img src={sl.media_url} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 text-left">
                    <p className="text-xs font-medium text-[#0A0A0A]">{t("stories_extra.slide_n", { n: i + 1, defaultValue: `Bild ${i + 1}` })}</p>
                    <p className="text-[11px] text-[#999] flex items-center gap-2">
                      <span className="flex items-center gap-1"><Eye size={11} /> {sl.view_count}</span>
                      {sl.reaction_count > 0 && <span className="flex items-center gap-1"><Heart size={11} className="text-red-400" /> {sl.reaction_count}</span>}
                    </p>
                  </div>
                  <ChevronRight size={16} className={`text-[#CCC] transition-transform ${openSlide === i ? "rotate-90" : ""}`} />
                </button>
                {openSlide === i && (
                  <div data-testid={`insights-viewers-${i}`} className="px-2 pb-2 space-y-1 max-h-48 overflow-y-auto">
                    {sl.viewers.length === 0 && <p className="text-[11px] text-[#AAA] px-2 py-2">{t("stories_extra.no_viewers", { defaultValue: "Noch keine Zuschauer" })}</p>}
                    {sl.viewers.map((v) => (
                      <div key={v.viewer_id} className="flex items-center gap-2 px-2 py-1.5">
                        {v.avatar_url
                          ? <img src={v.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                          : <div className="w-7 h-7 rounded-full bg-[#E5E5E5] flex items-center justify-center text-[10px] text-[#999]">{(v.display_name || "?")[0]}</div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#0A0A0A] truncate">{v.display_name}</p>
                          {v.handle && <p className="text-[10px] text-[#AAA] truncate">@{v.handle}</p>}
                        </div>
                        {v.reaction && <span className="text-base leading-none" data-testid="viewer-reaction">{v.reaction}</span>}
                        {v.is_follower && (
                          <span data-testid="viewer-follower-badge" className="text-[9px] font-semibold text-[#3B82F6] bg-[#EFF6FF] px-1.5 py-0.5 rounded-full">
                            {t("stories_extra.follower", { defaultValue: "Follower" })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ Highlight-Erstellen-Modal ============================ */
function CreateHighlightModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [slides, setSlides] = useState([]);
  const [selected, setSelected] = useState({}); // media_url -> slide
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studio/social/stories/archive");
        const seen = new Set(); const flat = [];
        for (const st of (data.stories || [])) {
          for (const sl of (st.slides || [])) {
            if (sl.media_url && !seen.has(sl.media_url)) { seen.add(sl.media_url); flat.push(sl); }
          }
        }
        setSlides(flat);
      } catch (_) { setSlides([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (sl) => setSelected((prev) => {
    const n = { ...prev };
    if (n[sl.media_url]) delete n[sl.media_url]; else n[sl.media_url] = sl;
    return n;
  });

  const create = async () => {
    const picked = Object.values(selected);
    if (picked.length === 0) return;
    setSaving(true);
    try {
      await api.post("/studio/social/highlights", {
        title: title.trim() || t("stories_extra.highlight", { defaultValue: "Highlight" }),
        cover_url: picked[0].media_url,
        slides: picked.map((s) => ({ media_url: s.media_url, media_type: s.media_type, effects: s.effects, music: s.music, caption: s.caption })),
      });
      onCreated();
    } catch (_) { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="create-highlight-modal" className="fixed inset-0 z-[72] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0]">
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("stories_extra.new_highlight", { defaultValue: "Neues Highlight" })}</p>
          <button data-testid="create-highlight-close" onClick={onClose} className="text-[#999]"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <input data-testid="highlight-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60}
            placeholder={t("stories_extra.highlight_title_ph", { defaultValue: "Titel (z. B. Reisen, Essen …)" })}
            className="w-full h-10 px-3 border border-[#E5E5E5] rounded-xl text-sm focus:border-[#0A0A0A] outline-none" />
          <p className="text-[11px] text-[#999]">{t("stories_extra.pick_slides", { defaultValue: "Wähle Elemente aus deinen vergangenen Storys aus:" })}</p>
          {loading ? (
            <p className="text-center text-xs text-[#999] py-8">…</p>
          ) : slides.length === 0 ? (
            <p className="text-center text-xs text-[#AAA] py-8">{t("stories_extra.no_archive", { defaultValue: "Noch keine vergangenen Storys vorhanden." })}</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {slides.map((sl, i) => {
                const on = !!selected[sl.media_url];
                return (
                  <button key={sl.media_url} data-testid={`highlight-pick-${i}`} onClick={() => toggle(sl)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${on ? "border-[#0A0A0A]" : "border-transparent"}`}>
                    {sl.media_type === "VIDEO"
                      ? <video src={sl.media_url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                      : <img src={sl.media_url} alt="" className="w-full h-full object-cover" />}
                    {on && <span className="absolute inset-0 bg-black/30 flex items-center justify-center"><Check size={18} className="text-white" /></span>}
                  </button>
                );
              })}
            </div>
          )}
          <button data-testid="highlight-create-btn" disabled={saving || Object.keys(selected).length === 0} onClick={create}
            className="w-full h-10 bg-[#0A0A0A] text-white text-sm font-medium rounded-xl disabled:opacity-40">
            {saving ? "…" : t("stories_extra.create_highlight", { count: Object.keys(selected).length, defaultValue: `Highlight erstellen (${Object.keys(selected).length})` })}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ Highlights-Verwaltung (Studio) ============================ */
export function StoryHighlightsManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState(null); // highlight_id to preview

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/studio/social/highlights"); setItems(data.highlights || []); }
    catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rename = async (h) => {
    const title = window.prompt(t("stories_extra.rename_prompt", { defaultValue: "Neuer Titel:" }), h.title);
    if (title == null) return;
    await api.put(`/studio/social/highlights/${h.highlight_id}`, { title });
    load();
  };
  const remove = async (h) => {
    if (!window.confirm(t("stories_extra.delete_highlight_confirm", { defaultValue: "Dieses Highlight löschen?" }))) return;
    await api.delete(`/studio/social/highlights/${h.highlight_id}`);
    load();
  };

  return (
    <div data-testid="highlights-manager" className="space-y-3 pt-4 border-t border-[#F0F0F0]">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1"><Star size={13} /> {t("stories_extra.highlights", { defaultValue: "Highlights" })}</p>
        <button data-testid="add-highlight-btn" onClick={() => setCreating(true)} className="text-[11px] text-[#0A0A0A] flex items-center gap-1 hover:underline">
          <Plus size={12} /> {t("stories_extra.new", { defaultValue: "Neu" })}
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-[#999]">…</p>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-[#AAA]">{t("stories_extra.highlights_hint", { defaultValue: "Hefte abgelaufene Story-Elemente dauerhaft auf dein Profil." })}</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((h) => (
            <div key={h.highlight_id} data-testid={`highlight-item-${h.highlight_id}`} className="shrink-0 w-16 text-center">
              <button onClick={() => setView(h.highlight_id)} className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#0A0A0A] p-0.5">
                <img src={h.cover_url} alt="" className="w-full h-full rounded-full object-cover" />
              </button>
              <p className="text-[10px] text-[#666] truncate mt-1">{h.title}</p>
              <div className="flex items-center justify-center gap-2 mt-0.5">
                <button data-testid={`highlight-rename-${h.highlight_id}`} onClick={() => rename(h)} className="text-[#999] hover:text-[#0A0A0A]"><Pencil size={11} /></button>
                <button data-testid={`highlight-delete-${h.highlight_id}`} onClick={() => remove(h)} className="text-red-400 hover:text-red-600"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <CreateHighlightModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {view && <HighlightViewer highlightId={view} onClose={() => setView(null)} />}
    </div>
  );
}

/* ============================ Highlight-Viewer (Vollbild) ============================ */
export function HighlightViewer({ highlightId, onClose }) {
  const { t } = useTranslation();
  const [hl, setHl] = useState(null);
  const [idx, setIdx] = useState(0);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/social/highlights/${highlightId}`); setHl(data); }
      catch (_) { setErr(true); }
    })();
  }, [highlightId]);

  const slides = hl?.slides || [];
  const s = slides[idx];
  const go = useCallback((d) => {
    const n = idx + d;
    if (n < 0) return;
    if (n >= slides.length) { onClose(); return; }
    setIdx(n);
  }, [idx, slides.length, onClose]);

  // Auto-advance für Bilder (4s); Videos wechseln per onEnded.
  useEffect(() => {
    if (!s || s.media_type === "VIDEO") return;
    const id = setTimeout(() => go(1), 4000);
    return () => clearTimeout(id);
  }, [s, idx, go]);

  if (err) return (
    <div data-testid="highlight-viewer" className="fixed inset-0 z-[80] bg-black flex items-center justify-center" onClick={onClose}>
      <p className="text-white/70 text-sm">{t("stories_extra.highlight_unavailable", { defaultValue: "privat / nicht verfügbar" })}</p>
    </div>
  );
  if (!hl) return <div className="fixed inset-0 z-[80] bg-black" />;
  const stickers = s?.effects?.stickers || [];

  return (
    <div data-testid="highlight-viewer" className="fixed inset-0 z-[80] bg-black flex flex-col">
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
            <div className="h-full bg-white" style={{ width: i <= idx ? "100%" : "0%" }} />
          </div>
        ))}
      </div>
      <div className="absolute top-4 left-3 right-3 z-30 flex items-center gap-2 mt-2">
        {hl.owner?.avatar_url
          ? <img src={hl.owner.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
          : <div className="w-7 h-7 rounded-full bg-white/20" />}
        <span className="text-white text-xs font-medium">{hl.title}</span>
        <div className="flex-1" />
        <button data-testid="highlight-viewer-close" onClick={onClose} className="text-white/90"><X size={20} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center relative">
        <div className="relative inline-block">
          {s?.media_type === "VIDEO"
            ? <video key={s.media_url} src={s.media_url} autoPlay muted playsInline style={{ filter: filterCss(s?.effects?.filter || "none") }} className="block max-w-[100vw] max-h-[100vh]" onEnded={() => go(1)} />
            : <img key={s?.media_url} src={s?.media_url} alt="" style={{ filter: filterCss(s?.effects?.filter || "none") }} className="block max-w-[100vw] max-h-[100vh] object-contain" />}
          {stickers.map((st, si) => (
            <div key={si} className="absolute z-20 leading-none pointer-events-none"
              style={{ left: `${(st.x ?? 0.5) * 100}%`, top: `${(st.y ?? 0.5) * 100}%`, transform: "translate(-50%,-50%)", fontSize: `${st.size || 56}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }}>
              {st.emoji}
            </div>
          ))}
        </div>
        <button aria-label="prev" onClick={() => go(-1)} className="absolute left-0 top-0 bottom-0 w-1/3" />
        <button aria-label="next" onClick={() => go(1)} className="absolute right-0 top-0 bottom-0 w-1/3" />
        {idx > 0 && <ChevronLeft size={20} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />}
        <ChevronRight size={20} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
      </div>
      {s?.caption && (
        <div className="absolute bottom-8 left-4 right-4 z-20 text-white text-sm text-center bg-gradient-to-t from-black/80 to-transparent py-4 pointer-events-none">{s.caption}</div>
      )}
    </div>
  );
}

/* ============================ Profil-Highlights-Reihe (öffentlich) ============================ */
export function ProfileHighlightsRow({ profileId }) {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);

  useEffect(() => {
    if (!profileId) return;
    (async () => {
      try { const { data } = await api.get(`/social/profiles/${profileId}/highlights`); setItems(data.highlights || []); }
      catch (_) { setItems([]); }
    })();
  }, [profileId]);

  if (items.length === 0) return null;
  return (
    <div data-testid="profile-highlights-row" className="flex gap-4 overflow-x-auto px-4 py-3 border-b border-[#F0F0F0]">
      {items.map((h) => (
        <button key={h.highlight_id} data-testid={`profile-highlight-${h.highlight_id}`} onClick={() => setView(h.highlight_id)} className="shrink-0 w-16 text-center">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#0A0A0A] p-0.5">
            <img src={h.cover_url} alt="" className="w-full h-full rounded-full object-cover" />
          </div>
          <p className="text-[10px] text-[#666] truncate mt-1">{h.title}</p>
        </button>
      ))}
      {view && <HighlightViewer highlightId={view} onClose={() => setView(null)} />}
    </div>
  );
}
